import classNames from "classnames";
import classes from "./Tree.module.less";
import { createContext, type CSSProperties, useContext, useMemo } from "react";
import { IconButton } from "@mui/material";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";

export interface Node {
  key: string;
  children: Node[];
}

export interface TreeProps<T extends Node> {
  className?: string;
  expanded?: string[];
  onToggleExpandItem: (key: string, expanded: boolean) => void;
  selected?: string[];
  onSelectItem: (key: T, selected: boolean) => void;
  // multiSelect?: boolean;
  items?: T[];
  renderItem?: (item: T, depth: number) => React.ReactNode;
}

const DEFAULT_RENDER_ITEM = <T extends Node>(item: T, depth: number) => <div>{item.key}</div>;

export const Tree = <T extends Node>({
  items,
  renderItem = DEFAULT_RENDER_ITEM<T>,
  className,
  expanded,
  onToggleExpandItem,
  selected,
  onSelectItem,
}: TreeProps<T>) => {
  const ctxVal = useMemo(
    () => ({ expanded, onToggleExpandItem, selected, onSelectItem }),
    [expanded, onToggleExpandItem, selected, onSelectItem],
  );
  if (!items) {
    return null;
  }

  return (
    // @ts-ignore
    <Context.Provider value={ctxVal}>
      <div className={classNames(classes.root, className)}>
        {items.map((item) => (
          <TreeItem item={item} depth={0} renderItem={renderItem} />
        ))}
      </div>
    </Context.Provider>
  );
};

type ContextProps = Pick<TreeProps<Node>, "expanded" | "onToggleExpandItem" | "selected" | "onSelectItem">;

const Context = createContext<ContextProps>({
  expanded: [],
  onToggleExpandItem: () => {},
  selected: [],
  onSelectItem: () => {},
});

interface TreeItemProps<T extends Node> {
  item: T;
  renderItem: (item: T, depth: number) => React.ReactNode;
  depth: number;
}
const TreeItem = <T extends Node>({ item, renderItem, depth }: TreeItemProps<T>) => {
  const { expanded, selected, onToggleExpandItem, onSelectItem } = useContext(Context);
  const isExpanded = expanded?.includes(item.key);
  const isSelected = selected?.includes(item.key);

  const style = { "--depth": depth } as CSSProperties;

  return (
    <>
      <div className={classNames(classes.item, isSelected && classes.selected)} style={style}>
        <div className={classes.buttonContainer} hidden={item.children.length === 0}>
          <IconButton color={"primary"} onClick={() => onToggleExpandItem(item.key, !isExpanded)}>
            <Icon type={isExpanded ? IconType.ChevronUp : IconType.ChevronDown} />
          </IconButton>
        </div>
        <div className={classes.dataContainer} onClick={() => onSelectItem(item, !isSelected)}>
          {renderItem(item, depth)}
        </div>
      </div>
      {isExpanded &&
        item.children.map((child) => <TreeItem item={child as T} depth={depth + 1} renderItem={renderItem} />)}
    </>
  );
};
